/**
 * R9 WS-B N-e §4.1/§4.2 — **groups**, screens 1 and 2 of the 17-screen port.
 *
 * Every path is the **PROXY** path (`site-admin-api/src/modules/permissions/permissions-proxy.controller.ts`,
 * `@Controller('v1/admin/permissions')`). ⛔ The producer's own routes are `/v1/admin/groups*` on
 * cp-auth-api and are **NOT reachable from the browser** — site-admin-api owns `/v1/admin` at the
 * gateway and declares no such route, so they answer a JSON 404. "Simplifying" a path to the
 * producer's ships a screen that 404s on load.
 *
 * | endpoint | proxy | permission |
 * |---|---|---|
 * | `GET    …/groups`                          | `@Get('groups')`                       | `cp:group:read` |
 * | `GET    …/groups/:id`                      | `@Get('groups/:id')`                   | `cp:group:read` |
 * | `POST   …/groups`                          | `@Post('groups')`                      | `cp:group:create` |
 * | `PATCH  …/groups/:id`                      | `@Patch('groups/:id')`                 | `cp:group:update` |
 * | `DELETE …/groups/:id`                      | `@Delete('groups/:id')` **204**        | `cp:group:delete` |
 * | `GET    …/groups/:id/members`              | `@Get('groups/:id/members')`           | `cp:group:read` |
 * | `POST   …/groups/:id/members`              | `@Post('groups/:id/members')`          | `cp:group:manage-members` |
 * | `DELETE …/groups/:id/members/:operatorId`  | `@Delete(…)` **204**                   | `cp:group:manage-members` |
 * | `GET    …/roles/groups/:groupId`           | `@Get('roles/groups/:groupId')`        | ⛔ **`cp:role:read`** |
 * | `POST   …/roles/:slug/groups/:groupId`     | `@Post('roles/:slug/groups/:groupId')` | ⛔ **`cp:permission:grant`** |
 * | `DELETE …/roles/:slug/groups/:groupId`     | `@Delete(…)` **204**                   | ⛔ **`cp:permission:grant`** |
 *
 * ⭐ Every one was read off the controller on 2026-08-07, not inferred from the old console's manifest
 * — SR-1. That single step found three live gate defects across the 17 screens, and **all three failed
 * in the same direction**: a door onto a 403, or a screen hidden from someone entitled to it.
 *
 * # ⛔⛔ A NEW MODULE, AND IT IS A GATE-INTEGRITY DECISION RATHER THAN FILE TIDINESS
 *
 * `permission-gate-parity.test.ts` discharges a component's obligation if it gates on **any one**
 * permission its imported api module declares. `identity.api.ts` declares SIX read grants, so a Groups
 * screen importing it would satisfy the gate on `cp:operator:read` and **prove nothing about the group
 * grant**. A module declaring only the `cp:group:*` family makes the check sharp.
 *
 * ⇒ ⛔ **Do not consolidate this back into `identity.api.ts`.** The granularity IS the check.
 *
 * # ⛔⛔ THREE PERMISSIONS GOVERN THREE PANELS OF ONE SCREEN — AND ONE GATE WOULD BE WRONG BOTH WAYS
 *
 * The detail screen's Roles panel reads `cp:role:read` and writes `cp:permission:grant`, neither of
 * which is a `cp:group:*` verb. ⇒ an operator holding `cp:group:read` and not `cp:role:read` gets the
 * screen with **that panel alone** forbidden and everything else working — the panel-by-panel
 * degradation `FleetCapacityPane` already establishes. ⛔ **Do not raise the entry gate to an `anyOf`
 * including `cp:role:read`**: that would hide the whole screen from someone entitled to most of it.
 */
import { api } from '../platform/http/client';

// ── The permissions, named once so a gate and a `ForbiddenState`'s copy cannot drift ──

export const GROUP_READ_PERMISSION = 'cp:group:read';
export const GROUP_CREATE_PERMISSION = 'cp:group:create';
export const GROUP_UPDATE_PERMISSION = 'cp:group:update';
export const GROUP_DELETE_PERMISSION = 'cp:group:delete';
export const GROUP_MANAGE_MEMBERS_PERMISSION = 'cp:group:manage-members';

/**
 * ⛔ The full literal, NOT `${PERMISSIONS_PATH}/groups`.
 *
 * `fixture-routes-answer.test.ts` derives its obligation by grepping `` `/v1/…` `` literals out of
 * `src/api/**`, so writing the path whole is what enrols this endpoint in that gate. A
 * prefix-composed path is invisible to it, which reads as *"no obligation"* rather than as
 * *"not covered"* — and a screen whose fixture 501s renders blank in the owner's review harness.
 */
export const GROUPS_PATH = '/v1/admin/permissions/groups';

export function groupPath(groupId: string): string {
  return `${GROUPS_PATH}/${encodeURIComponent(groupId)}`;
}

/**
 * ⛔ `/v1/admin/permissions/groups/:id/members`. The proxy forwards to cp-auth-api's
 * `/v1/admin/groups/:id/members` — **a different string** — and the producer's own path is not
 * reachable from the browser.
 *
 * ⚠️ **MOVED here from `identity.api.ts`, not copied.** It had zero production consumers there; leaving
 * a duplicate would create two answers to *"where do a group's members come from"*, and — worse for
 * the gate above — would let `cp:group:read` be satisfied from either module.
 */
export function groupMembersPath(groupId: string): string {
  return `${groupPath(groupId)}/members`;
}

/**
 * ⚠️ The proxy names this segment **`:operatorId`** even though the value you hold came back from a
 * read as `userId`. Same id space, different parameter name — see {@link addGroupMember} for the
 * matching asymmetry in the request BODY.
 */
export function groupMemberPath(groupId: string, operatorId: string): string {
  return `${groupMembersPath(groupId)}/${encodeURIComponent(operatorId)}`;
}

/** ⛔ `roles/groups/:groupId`, not `groups/:groupId/roles` — and gated `cp:role:read`. */
export function groupRolesPath(groupId: string): string {
  return `/v1/admin/permissions/roles/groups/${encodeURIComponent(groupId)}`;
}

/** ⛔ `roles/:slug/groups/:groupId` — the role comes FIRST. Both verbs are `cp:permission:grant`. */
export function roleGroupPath(slug: string, groupId: string): string {
  return `/v1/admin/permissions/roles/${encodeURIComponent(slug)}/groups/${encodeURIComponent(groupId)}`;
}

// ── The wire shapes ──────────────────────────────────────────────────────────

/**
 * ⛔ A CLOSED union on the wire — `cp-auth-api/src/modules/groups/group.service.ts:20`.
 *
 * ⚠️ It is a real distinction and not provenance trivia: a `scim` group's membership is owned by the
 * IdP, and the producer refuses a member add or remove on it with a 409. The DETAIL screen is where that becomes
 * a verdict; on the list it is a **value**, and §0.4 rules that colour encodes a verdict and never a
 * value — a SCIM group is not *worse* than a manual one, it is differently governed.
 */
export type GroupSource = 'manual' | 'scim';

/** `SCIM_PROVIDERS` upstream (`permissions-proxy.schema.ts:23`), plus `null` for a manual group. */
export type GroupIdpProvider = 'google' | 'okta' | 'azure-ad' | null;

/**
 * `GroupView` — `group.service.ts:15-27`, built by `toGroupView`, which ISO-strings every timestamp.
 *
 * ⚠️ **`memberCount` is DERIVED by the producer** from a second query (`group.service.ts:78-88`), not
 * stored. It is therefore consistent with `members` on the detail read and cannot be stale relative
 * to it — which is why the list may state a count the detail does not have to re-derive.
 *
 * ⛔ **`lastSyncedAt: null` MEANS TWO DIFFERENT THINGS, discriminated on {@link GroupSource}.** On a
 * `manual` group it is correct and expected — nothing syncs it, and an em dash is the honest render.
 * On a `scim` group it means an IdP owns this group and **no sync has completed yet**, which is a
 * `notcollected` absence with a reason. ⭐ Two nulls, two meanings, one column: the interesting pair.
 */
export interface GroupView {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly description: string | null;
  readonly source: GroupSource;
  readonly idpProvider: GroupIdpProvider;
  readonly externalId: string | null;
  readonly lastSyncedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly memberCount: number;
}

/**
 * `GroupMemberView` — `group.service.ts:29-34`.
 *
 * ⛔⛔ **KEYED ON `userId`, AND THE WRITE SENDS `operatorId`. THAT IS NOT A TYPO.** See
 * {@link addGroupMember}.
 *
 * ⚠️ **MOVED here from `identity.api.ts`, not copied** — same reason as {@link groupMembersPath}.
 */
export interface GroupMemberView {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly addedAt: string;
}

/** ⛔ `GroupDetailView extends GroupView` with `members` INLINE — one call, not two. */
export interface GroupDetailView extends GroupView {
  readonly members: readonly GroupMemberView[];
}

/**
 * One `cp_auth.group_roles` row — ⚠️ **the RAW Drizzle table row**
 * (`db/schema/group-roles.ts:34`, returned straight out of `roles.controller.ts:73-80`), so the wire
 * shape IS the database schema and a renamed column changes the response with no view layer in
 * between. Same exposure `identity.api.ts` records for `ResourceGrantRow` and `RoleDefinition`.
 *
 * `grantedAt` is a `mode: 'date'` column ⇒ an ISO **string** over JSON.
 */
export interface GroupRoleRow {
  readonly groupId: string;
  readonly roleSlug: string;
  readonly grantedAt: string;
  readonly grantedBy: string | null;
}

/** Body for `POST groups` — `createGroupSchema`, `.strict()` upstream (name ≤ 120, description ≤ 500). */
export interface CreateGroupBody {
  readonly name: string;
  readonly description?: string;
}

/**
 * Body for `PATCH groups/:id` — `updateGroupSchema`, `.strict()` upstream.
 *
 * ⛔ `description: null` is a REAL value, not an omission: the schema is `.nullable().optional()`, so
 * `null` is the explicit **clear-the-description** signal and leaving the key out means *"do not
 * touch it"*. Collapsing the two would make it impossible to remove a description.
 */
export interface UpdateGroupBody {
  readonly name?: string;
  readonly description?: string | null;
}

// ── The reads ────────────────────────────────────────────────────────────────

/** ⛔ A bare ARRAY, ordered by NAME (`orderBy(asc(groups.name))`). Not an envelope, not paginated. */
export function listGroups(): Promise<readonly GroupView[]> {
  return api.get<readonly GroupView[]>(GROUPS_PATH);
}

export function getGroup(groupId: string): Promise<GroupDetailView> {
  return api.get<GroupDetailView>(groupPath(groupId));
}

/**
 * ⚠️ **MOVED here from `identity.api.ts`, not copied.**
 *
 * ⛔ Redundant with {@link getGroup} on the detail screen, which inlines `members` — kept because it is
 * a real, separately-gated endpoint (`cp:group:read`) and because deleting a producer's route from the
 * client layer is how the next surface concludes it does not exist.
 */
export function listGroupMembers(groupId: string): Promise<readonly GroupMemberView[]> {
  return api.get<readonly GroupMemberView[]>(groupMembersPath(groupId));
}

/** ⛔ `cp:role:read`, NOT a `cp:group:*` verb. The Roles panel degrades independently of the screen. */
export function listGroupRoles(groupId: string): Promise<readonly GroupRoleRow[]> {
  return api.get<readonly GroupRoleRow[]>(groupRolesPath(groupId));
}

// ── The writes ───────────────────────────────────────────────────────────────

/**
 * `cp:group:create`.
 *
 * ⛔ **409 IS A REAL ANSWER, NOT A TRANSPORT FAULT.** `GroupService.create` refuses a second MANUAL
 * group with the same name (`ConflictException`), and the proxy preserves the 409 rather than
 * flattening it. ⇒ the create dialog must render the conflict as *"that name is taken"* and not as a
 * generic failure — the operator's remedy is to pick another name, which a generic error does not say.
 * ⚠️ The uniqueness is scoped to `source: 'manual'`, so a manual group MAY share a name with a
 * SCIM-imported one.
 */
export function createGroup(body: CreateGroupBody): Promise<GroupView> {
  return api.post<GroupView>(GROUPS_PATH, body);
}

/** `cp:group:update`. ⛔ A separately-holdable verb from `cp:group:read` — gate the control, not the door. */
export function updateGroup(groupId: string, body: UpdateGroupBody): Promise<GroupView> {
  return api.patch<GroupView>(groupPath(groupId), body);
}

/** `cp:group:delete`. ⛔ **204** (`@HttpCode(NO_CONTENT)`) ⇒ genuinely no body. */
export function deleteGroup(groupId: string): Promise<void> {
  return api.delete<void>(groupPath(groupId));
}

/**
 * `cp:group:manage-members`.
 *
 * # ⛔⛔ THE ASYMMETRY, AND IT IS NOT A TYPO
 *
 * This sends **`{ operatorId }`**; every read comes back keyed on **`userId`**. The proxy translates —
 * `permissions-proxy.service.ts` `addGroupMember` forwards `{ userId: operatorId }` to cp-auth-api —
 * and the DELETE route's path segment is `:operatorId` while the row it removes is a `userId`.
 *
 * ⇒ ⛔ **Send `operatorId`, read back `userId`.** "Normalising" either side silently breaks add-member
 * with a 400 naming a field the reader will not believe is wrong, because the response they are
 * looking at uses the other name.
 */
export function addGroupMember(groupId: string, operatorId: string): Promise<GroupMemberView> {
  return api.post<GroupMemberView>(groupMembersPath(groupId), { operatorId });
}

/**
 * `cp:group:manage-members`. ⛔ **204.**
 *
 * ⚠️ The path segment is the proxy's `:operatorId` even though the value you hold is a `userId` from
 * the read — see {@link groupMemberPath}. Pinned in a test rather than left to a reader to notice.
 */
export function removeGroupMember(groupId: string, userId: string): Promise<void> {
  return api.delete<void>(groupMemberPath(groupId, userId));
}

/**
 * ⛔ **`cp:permission:grant`, NOT `cp:group:update`.** Changing what a group CONFERS is a different
 * authority from renaming it, and the producer gates them separately — collapsing the two would show
 * a role editor to someone the server will refuse.
 */
export function assignRoleToGroup(slug: string, groupId: string): Promise<GroupRoleRow> {
  return api.post<GroupRoleRow>(roleGroupPath(slug, groupId));
}

/** ⛔ `cp:permission:grant`, and **204**. */
export function revokeRoleFromGroup(slug: string, groupId: string): Promise<void> {
  return api.delete<void>(roleGroupPath(slug, groupId));
}
